import type { SsoPolicy, SsoUpstream } from "@/lib/api";

// Words for app sign-in. User-visible copy follows the product voice: "Boo",
// never "AI" / "agent" / "container". Say who gets in, in plain terms.

export const UPSTREAM_LABELS: Record<SsoUpstream, string> = {
  google: "Google",
  microsoft: "Microsoft",
  slack: "Slack",
  email: "Email code",
};

const orList = (names: string[]) =>
  names.length <= 1 ? (names[0] ?? "") : `${names.slice(0, -1).join(", ")} or ${names.at(-1)}`;

/** "Google", "Google or Microsoft", "Google, Microsoft or an email code". */
export function providerList(upstreams: SsoUpstream[]): string {
  return orList(upstreams.map((u) => (u === "email" ? "an email code" : UPSTREAM_LABELS[u])));
}

/** "a Google account", "a Google or Microsoft account", "a Google account or an email code", "an email code". */
export function accountPhrase(upstreams: SsoUpstream[]): string {
  const accounts = upstreams.filter((u) => u !== "email").map((u) => UPSTREAM_LABELS[u]);
  const parts = accounts.length ? [`a ${orList(accounts)} account`] : [];
  if (upstreams.includes("email")) parts.push("an email code");
  return orList(parts);
}

const listText = (items: string[], max = 2) =>
  items.length <= max ? items.join(", ") : `${items.slice(0, max).join(", ")} and ${items.length - max} more`;

// What each choice in the editor is: the policy's mode. "members" is the
// default when nothing is saved. Every mode means exactly what it says.
export type AccessChoice = SsoPolicy["mode"];

export const choiceOf = (policy: SsoPolicy): AccessChoice => policy.mode;

/** One short phrase for who can open an app. */
export function policySummary(policy: SsoPolicy): string {
  switch (policy.mode) {
    case "members":
      return "Your team";
    case "org_domain":
      return `Anyone at ${listText(policy.domains)}`;
    case "allowlist":
      return policy.emails.length === 1 ? policy.emails[0]! : `${policy.emails.length} people`;
    case "roster":
      return "Your Slack workspace";
    case "open":
      return "Anyone who signs in";
  }
}

const DOMAIN_RE = /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const domainError = (d: string) => (DOMAIN_RE.test(d) ? null : `“${d}” isn't a domain, like acme.com.`);
export const emailError = (e: string) =>
  e.length <= 254 && EMAIL_RE.test(e) ? null : `“${e}” isn't an email address.`;
